import {
  Children,
  isValidElement,
  useMemo,
  type ReactElement,
  type ReactNode,
} from 'react';

import { isSlot } from '../../../core/utils/is-slot.js';
import {
  SelectV2Content,
  type SelectV2ContentProps,
} from '../SelectContent.js';
import { SelectV2CustomTrigger } from '../SelectCustomTrigger.js';
import { SelectV2DisplayValue } from '../SelectDisplayValue.js';
import {
  SelectV2EmptyState,
  type SelectV2EmptyStateProps,
} from '../SelectEmptyState.js';
import { SelectV2Filter, type SelectV2FilterProps } from '../SelectFilter.js';
import { SelectV2Prefix, type SelectV2PrefixProps } from '../SelectPrefix.js';
import {
  SelectV2Trigger,
  type SelectV2TriggerProps,
} from '../SelectTrigger.js';

interface SelectV2Slots {
  Content: ReactElement<SelectV2ContentProps> | null;
  CustomTrigger: ReactElement<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { [key: string]: any } | null | undefined
  > | null;
  DisplayValue: ReactNode[] | null;
  Trigger: ReactElement<SelectV2TriggerProps> | null;
  Prefix: ReactElement<SelectV2PrefixProps> | null;
  Filter: ReactElement<SelectV2FilterProps> | null;
  EmptyState: ReactElement<SelectV2EmptyStateProps> | null;
}

/**
 * Helper hook that will extract the Slots used in the select and collect the data / components.
 *
 * @internal
 */
export function useSelectV2Slots(children: ReactNode): SelectV2Slots {
  return useMemo(() => {
    const slots: SelectV2Slots = {
      Content: null,
      CustomTrigger: null,
      DisplayValue: null,
      Trigger: null,
      Prefix: null,
      Filter: null,
      EmptyState: null,
    };

    Children.forEach(children, (child) => {
      if (isSlot(child, SelectV2Content)) {
        slots.Content = child;
        // DISCUSS: Is this an issue when there are a lot of select options?
        Children.forEach(child.props.children, (child) => {
          if (isSlot(child, SelectV2Filter)) {
            slots.Filter = child;
            return;
          } else if (isSlot(child, SelectV2EmptyState)) {
            slots.EmptyState = child;
          }
        });
        return;
      } else if (isSlot(child, SelectV2Trigger)) {
        slots.Trigger = child;

        Children.forEach(child.props.children, (child) => {
          if (isSlot(child, SelectV2DisplayValue)) {
            Children.forEach(child.props.children, (child) => {
              if (isSlot(child, SelectV2Prefix)) {
                slots.Prefix = child;
                return;
              }

              // Concatenate free form content in the
              slots.DisplayValue = slots.DisplayValue || [];
              slots.DisplayValue.push(child);
            });
          }
        });
      } else if (isSlot(child, SelectV2CustomTrigger)) {
        // The custom trigger should only use the first child.
        // TODO: What about fragments etc?
        const triggerValue = Children.toArray(child.props.children)[0];
        if (isValidElement<SelectV2Slots['CustomTrigger']>(triggerValue)) {
          slots.CustomTrigger = triggerValue;
        }
      } else if (isSlot(child, SelectV2Filter)) {
        slots.Filter = child;
      }
    });

    return slots;
  }, [children]);
}
